import type { BrandTwoLetterCode, RegionCode } from "brandRegionConfig";
import { getBrandTwoLetterCodeFromHost } from "core/utils/getBrandTwoLetterCodeFromHost";
import { isValidRegion } from "core/utils/isValidRegion";
import { fetchPitstopConfigurationWithCache } from "./fetchCachedPitstopConfiguration";
import { trackServerException } from "./trackServerException";

/**
 *! IMPORTANT MUST ONLY BE CALLED SERVER SIDE
 */
export async function getPitstopConfig(host: string | undefined): Promise<
  | {
      ok: true;
      brandTwoLetterCode: BrandTwoLetterCode;
      availableRegions: RegionCode[];
    }
  | { ok: false; error: { message: string } }
> {
  try {
    if (!host) {
      throw new Error(`Unable to determine host from request`);
    }
    const brandTwoLetterCode = getBrandTwoLetterCodeFromHost(host);
    if (!brandTwoLetterCode) {
      throw new Error(
        `Unable to determine brandTwoLetterCode from host: "${host}"`
      );
    }
    const config = await fetchPitstopConfigurationWithCache();
    const regions = config[brandTwoLetterCode];

    if (!regions) {
      throw new Error(`No entry for brand code "${brandTwoLetterCode}" found`);
    }

    const availableRegions = regions
      .map((region) => region.toLowerCase())
      .filter((potentialRegion) => isValidRegion(potentialRegion));
    if (availableRegions.length === 0) {
      throw new Error(
        `No available regions configured for brand code "${brandTwoLetterCode}"`
      );
    }
    return { ok: true, brandTwoLetterCode, availableRegions };
  } catch (e: unknown) {
    trackServerException({ exception: e as Error });
    if (e instanceof Error) {
      return {
        ok: false,
        error: {
          message: e.message,
        },
      };
    }
    return {
      ok: false,
      error: { message: "An unknown error occurred" },
    };
  }
}
